import {ShieldCheckIcon, ShieldIcon, ShieldSlashIcon} from '@primer/octicons-react'
import {RepositoryAndAlertPicker} from './RepositoryAndAlertPicker'
import type {BaseAlert} from './AlertPicker'
import {codeScanningOrgRepositoryListPath, codeScanningAlertPickerPath} from '@github-ui/paths'
import type {AlertsQueryRequest} from '../hooks/use-alerts-query'

export type CodeScanningAlertStatus = 'OPEN' | 'FIXED' | 'DISMISSED'

export interface CodeScanningAlert extends BaseAlert {
  state: CodeScanningAlertStatus
}

export function getAlertKey(alert: CodeScanningAlert): string {
  return `code_scanning/${alert.repository.nameWithOwner}/${alert.number}`
}

function getLeadingVisual(alert: CodeScanningAlert) {
  switch (alert.state) {
    case 'FIXED':
      return <ShieldCheckIcon className="fgColor-done" />
    case 'DISMISSED':
      return <ShieldSlashIcon className="fgColor-closed" />
    default:
      return <ShieldIcon className="fgColor-open" />
  }
}

// If the filter looks like "https://:hostname/:owner/:repo/security/code-scanning/:number" then parse out those values.
// Otherwise treat it as a query against the current owner/repo.
function getAlertsQueryRequest(currentOwner: string, currentRepo: string, filter: string): AlertsQueryRequest {
  const codeScanningAlertUrlPattern = new RegExp(
    `https://${window.location.hostname}/([^/]+)/([^/]+)/security/code-scanning/(\\d+)`,
  )
  filter = filter.trim()
  const match = filter.match(codeScanningAlertUrlPattern)
  if (match) {
    const [, owner, repo, numberString] = match
    const number = parseInt(numberString ?? '', 10)
    return {
      path: codeScanningAlertPickerPath({owner: owner ?? '', repo: repo ?? ''}),
      number,
    }
  } else {
    return {
      path: codeScanningAlertPickerPath({owner: currentOwner, repo: currentRepo}),
      query: filter,
    }
  }
}

interface CodeScanningAlertPickerProps {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  onAlertSelection: (alerts: CodeScanningAlert[]) => void
  initialOwner: string
  initialRepo: string
  initialAlertSelection: CodeScanningAlert[]
}

// Currently CodeScanningAlertPicker is the public interface for this package,
// but in the future we plan to add support for other security alert types.
// When that happens, there will be another component wrapping this which allows
// selecting the alert type and switching between sub-components, similar to how
// RepositoryAndAlertPicker wraps AlertPicker.
export function CodeScanningAlertPicker(props: CodeScanningAlertPickerProps) {
  return (
    <RepositoryAndAlertPicker<CodeScanningAlert>
      {...props}
      getAlertsQueryRequest={getAlertsQueryRequest}
      getAlertRepositoriesPath={owner => codeScanningOrgRepositoryListPath({org: owner})}
      getAlertKey={getAlertKey}
      getLeadingVisual={getLeadingVisual}
    />
  )
}
